import React, { useState, useCallback } from 'react';
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import { useAuth } from '../../contexts/AuthContext';

interface BulkImportUploadProps {
  enterpriseId: string;
  onBack?: () => void;
}

interface AnalysisResult {
  analysisId: string;
  fileName: string;
  rowCount: number;
  fieldMappings: Record<string, string>;
  dataIssues: any[];
  categorySuggestions: any[];
  beerItems: any[];
  beerLogos: any[];
  rawData: any[];
  analysis: {
    fieldMappings: Record<string, string>;
    dataIssues: any[];
    categorySuggestions: any[];
    beerItems: any[];
  };
}

const parseCSVFile = async (file: File): Promise<any[]> => {
  // Only accept CSV files for now
  if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
    throw new Error('Excel files are not supported yet. Please upload a CSV file.');
  }
  
  // Parse CSV files
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        
        // Handle different line endings (Windows \r\n, Unix \n, Mac \r)
        const lines = text.split(/\r?\n|\r/).filter(line => line.trim());
        
        if (lines.length < 2) {
          resolve([]);
          return;
        }
        
        // Improved CSV parsing to handle quotes and commas in values
        const parseCSVLine = (line: string): string[] => {
          const result = [];
          let current = '';
          let inQuotes = false;
          
          for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];
            
            if (char === '"') {
              if (inQuotes && nextChar === '"') {
                // Handle escaped quotes ""
                current += '"';
                i++; // Skip next quote
              } else {
                inQuotes = !inQuotes;
              }
            } else if (char === ',' && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          
          // Don't forget the last value
          result.push(current.trim());
          return result;
        };
        
        const headers = parseCSVLine(lines[0]);
        console.log('CSV Headers:', headers);
        
        const data = lines.slice(1).map((line, lineIndex) => {
          if (!line.trim()) return null; // Skip empty lines
          
          const values = parseCSVLine(line);
          const row: any = {};
          
          headers.forEach((header, index) => {
            // Clean up header names (remove BOM, quotes, etc)
            const cleanHeader = header.replace(/^\uFEFF/, '').replace(/^["']|["']$/g, '').trim();
            const value = values[index] || '';
            // Clean up values (remove surrounding quotes)
            row[cleanHeader] = value.replace(/^["']|["']$/g, '').trim();
          });
          
          console.log(`Row ${lineIndex + 1}:`, row);
          return row;
        }).filter(row => row !== null); // Remove null entries from empty lines
        
        console.log('Parsed CSV data:', data);
        resolve(data);
      } catch (error) {
        console.error('CSV parsing error:', error);
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

const BulkImportUpload: React.FC<BulkImportUploadProps> = ({ 
  enterpriseId, 
  onBack 
}) => {
  const { getIdToken } = useAuth();
  const [currentStep, setCurrentStep] = useState<'upload' | 'analysis' | 'review' | 'importing'>('upload');
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  }, []);

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    // Validate file type - CSV only
    const allowedTypes = ['text/csv', 'application/csv'];
    const fileName = file.name.toLowerCase();
    
    // Check both MIME type and file extension (some systems report different MIME types)
    if (!allowedTypes.includes(file.type) && !fileName.endsWith('.csv')) {
      alert('Please upload a CSV file (.csv). Excel files are not supported yet.');
      return;
    }

    setUploading(true);

    try {
      // Convert file to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]); // Remove data URL prefix
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Determine file type
      const fileType = file.name.toLowerCase().endsWith('.csv') ? 'csv' : 'xlsx';

      // Get authentication token
      const token = await getIdToken();
      
      // Use the menu management API directly to create menu items from CSV data
      // For now, we'll parse the CSV client-side and create items directly
      const parsedData = await parseCSVFile(file);
      
      const result = { 
        jobId: `csv-job-${Date.now()}`,
        parsedData 
      };
      
      if (result.jobId && result.parsedData) {
        // Create field mappings based on CSV headers
        const headers = Object.keys(result.parsedData[0] || {});
        const autoFieldMappings: Record<string, string> = {};
        
        headers.forEach(header => {
          const lowerHeader = header.toLowerCase();
          // More comprehensive field detection
          if (lowerHeader.includes('name') || lowerHeader.includes('item') || lowerHeader === 'product' || lowerHeader === 'title') {
            autoFieldMappings[header] = 'name';
          } else if (lowerHeader.includes('desc') || lowerHeader.includes('description') || lowerHeader === 'details') {
            autoFieldMappings[header] = 'description';
          } else if (lowerHeader.includes('price') || lowerHeader.includes('cost') || lowerHeader === 'amount' || lowerHeader === 'value') {
            autoFieldMappings[header] = 'price';
          } else if (lowerHeader.includes('category') || lowerHeader.includes('type') || lowerHeader === 'group' || lowerHeader === 'section') {
            autoFieldMappings[header] = 'category';
          } else if (lowerHeader.includes('subcategory') || lowerHeader.includes('sub category') || lowerHeader.includes('sub-category')) {
            autoFieldMappings[header] = 'subcategory';
          } else if (lowerHeader.includes('allergen') || lowerHeader.includes('allergy') || lowerHeader.includes('contains')) {
            autoFieldMappings[header] = 'allergens';
          } else if (lowerHeader.includes('tag') || lowerHeader.includes('keyword')) {
            autoFieldMappings[header] = 'tags';
          } else if (lowerHeader.includes('calorie') || lowerHeader === 'cal' || lowerHeader === 'kcal') {
            autoFieldMappings[header] = 'calories';
          } else {
            autoFieldMappings[header] = 'ignore';
          }
        });
        
        const analysis: AnalysisResult = {
          analysisId: result.jobId,
          fileName: file.name,
          rowCount: result.parsedData.length,
          fieldMappings: autoFieldMappings,
          dataIssues: [],
          categorySuggestions: Array.from(new Set(result.parsedData.map(item => {
            // Find the category field based on our mapping
            const categoryField = Object.keys(autoFieldMappings).find(key => autoFieldMappings[key] === 'category');
            return categoryField && item[categoryField] ? item[categoryField] : 'Uncategorized';
          }))),
          beerItems: [],
          beerLogos: [],
          rawData: result.parsedData,
          analysis: {
            fieldMappings: autoFieldMappings,
            dataIssues: [],
            categorySuggestions: [],
            beerItems: []
          }
        };
        
        setAnalysis(analysis);
        setFieldMappings(autoFieldMappings);
        setSelectedItems(result.parsedData.map((_: any, index: number) => index));
        setCurrentStep('analysis');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleImport = async () => {
    if (!analysis) return;

    setImporting(true);
    setCurrentStep('importing');

    try {
      const approvedItems = selectedItems.map(index => analysis.rawData[index]);
      const token = await getIdToken();
      
      let importedCount = 0;
      
      // Create each menu item using the Menu Management API
      for (const item of approvedItems) {
        try {
          // Map the CSV data to menu item format
          const getFieldValue = (targetField: string) => {
            const originalColumn = Object.keys(fieldMappings).find(key => fieldMappings[key] === targetField);
            return originalColumn ? item[originalColumn] : '';
          };
          
          const menuItemData = {
            name: getFieldValue('name') || 'Unnamed Item',
            description: getFieldValue('description') || 'No description',
            category: getFieldValue('category') || 'Uncategorized',
            subcategory: getFieldValue('subcategory') || '',
            basePrice: parseFloat(getFieldValue('price').toString().replace(/[^0-9.]/g, '')) || 0,
            cost: 0,
            enterpriseId: enterpriseId,
            conceptId: null,
            franchiseeId: null,
            storeId: null,
            allergenInfo: getFieldValue('allergens') ? getFieldValue('allergens').split(',').map((s: string) => s.trim()) : [],
            preparationTime: 15,
            difficulty: 'medium',
            isActive: true,
            isAvailable: true,
            canBeModifier: false,
            tags: getFieldValue('tags') ? getFieldValue('tags').split(',').map((s: string) => s.trim()) : [],
            imageUrl: '',
            internalMenuItemId: `CSV-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
            posSequenceNumber: Math.floor(Math.random() * 10000)
          };
          
          const response = await fetch(API_ENDPOINTS.menuItems(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(menuItemData),
          });
          
          if (response.ok) {
            importedCount++;
          } else {
            console.error('Failed to create menu item:', await response.text());
          }
        } catch (error) {
          console.error('Error creating menu item:', error);
        }
      }
      
      const result = {
        success: importedCount > 0,
        importedCount
      };
      
      if (result.success) {
        alert(`Successfully imported ${result.importedCount} menu items!`);
        if (onBack) onBack();
      } else {
        throw new Error('Import failed');
      }
    } catch (error) {
      console.error('Import error:', error);
      alert(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setCurrentStep('review');
    } finally {
      setImporting(false);
    }
  };

  const toggleItemSelection = (index: number) => {
    setSelectedItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const selectAll = () => {
    if (!analysis) return;
    setSelectedItems(analysis.rawData.map((_: any, index: number) => index));
  };

  const selectNone = () => {
    setSelectedItems([]);
  };

  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <FileSpreadsheet className="w-16 h-16 mx-auto text-blue-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Excel/CSV File</h2>
        <p className="text-gray-600">Upload your menu data file to automatically analyze and import items with AI assistance.</p>
      </div>
      
      <div 
        className={`
          border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer transition-all
          ${dragActive ? 'border-blue-500 bg-blue-50' : 'hover:border-gray-400 hover:bg-gray-50'}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept=".csv,text/csv,application/csv"
          onChange={handleFileSelect}
          className="hidden"
          id="file-upload"
          disabled={uploading}
        />
        
        {uploading ? (
          <div className="space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-gray-600">Analyzing your file...</p>
          </div>
        ) : (
          <div className="space-y-4">
            <Upload className="w-12 h-12 text-gray-400 mx-auto" />
            <div>
              <label htmlFor="file-upload" className="text-blue-600 font-medium cursor-pointer hover:text-blue-500">
                Click to upload
              </label>
              <span className="text-gray-500"> or drag and drop</span>
            </div>
            <p className="text-sm text-gray-500">
              CSV files only (.csv) - Excel files coming soon
            </p>
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">CSV Format Requirements</h3>
        <p className="text-sm text-blue-700 mb-2">Your CSV file should have a header row with column names like:</p>
        <code className="text-xs bg-blue-100 p-2 rounded block">
          name,description,price,category,subcategory
        </code>
        <p className="text-sm text-blue-700 mt-2">
          The system will automatically detect common column names for menu items.
        </p>
      </div>
    </div>
  );

  const renderAnalysisStep = () => {
    if (!analysis) return null;

    return (
      <div className="space-y-6">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Analysis Complete</h2>
          <p className="text-gray-600">Review the AI analysis results and field mappings below.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{analysis.rowCount}</div>
            <div className="text-sm text-blue-700">Items Found</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{analysis.analysis.dataIssues?.length || 0}</div>
            <div className="text-sm text-red-700">Data Issues</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{analysis.analysis.categorySuggestions?.length || 0}</div>
            <div className="text-sm text-yellow-700">Category Suggestions</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-amber-600">{analysis.analysis.beerItems?.length || 0}</div>
            <div className="text-sm text-amber-700">Beer Items</div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Field Mappings</h3>
          <div className="space-y-3">
            {Object.entries(analysis.analysis.fieldMappings).map(([original, suggested]) => (
              <div key={original} className="flex items-center space-x-4">
                <div className="flex-1">
                  <span className="font-medium text-gray-700">{original}</span>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <div className="flex-1">
                  <select 
                    value={fieldMappings[original] || suggested}
                    onChange={(e) => setFieldMappings(prev => ({ ...prev, [original]: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="name">Name</option>
                    <option value="description">Description</option>
                    <option value="price">Price</option>
                    <option value="category">Category</option>
                    <option value="subcategory">Subcategory</option>
                    <option value="tags">Tags</option>
                    <option value="allergens">Allergens</option>
                    <option value="calories">Calories</option>
                    <option value="fat">Fat (g)</option>
                    <option value="protein">Protein (g)</option>
                    <option value="carbohydrates">Carbohydrates (g)</option>
                    <option value="fiber">Fiber (g)</option>
                    <option value="sugar">Sugar (g)</option>
                    <option value="sodium">Sodium (mg)</option>
                    <option value="ingredients">Ingredients</option>
                    <option value="dietary_info">Dietary Info</option>
                    <option value="prep_time">Prep Time</option>
                    <option value="cook_time">Cook Time</option>
                    <option value="ignore">Ignore</option>
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-between">
          <button 
            onClick={() => setCurrentStep('upload')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Back to Upload
          </button>
          <button 
            onClick={() => setCurrentStep('review')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Review Items
          </button>
        </div>
      </div>
    );
  };

  const renderReviewStep = () => {
    if (!analysis) return null;

    // Create reverse mapping from target fields to original columns
    const getFieldValue = (item: any, targetField: string) => {
      // Find the original column name that maps to this target field
      const originalColumn = Object.keys(fieldMappings).find(key => fieldMappings[key] === targetField);
      if (originalColumn && item[originalColumn] !== undefined) {
        return item[originalColumn];
      }
      
      // Fallback: try direct property access
      if (item[targetField] !== undefined) {
        return item[targetField];
      }
      
      // Last resort: try common column names
      if (targetField === 'name') {
        return item.name || item.Name || item.item_name || item['Item Name'] || item.title || item.Title;
      } else if (targetField === 'description') {
        return item.description || item.Description || item.desc || item.Desc;
      } else if (targetField === 'price') {
        return item.price || item.Price || item.cost || item.Cost;
      } else if (targetField === 'category') {
        return item.category || item.Category || item.type || item.Type;
      } else if (targetField === 'allergens') {
        return item.allergens || item.Allergens || item.allergies || item.Allergies || item.contains || item.Contains;
      } else if (targetField === 'calories') {
        return item.calories || item.Calories || item.cal || item.Cal;
      } else if (targetField === 'fat') {
        return item.fat || item.Fat || item.total_fat || item['Total Fat'];
      } else if (targetField === 'protein') {
        return item.protein || item.Protein || item.prot || item.Prot;
      } else if (targetField === 'carbohydrates') {
        return item.carbohydrates || item.Carbohydrates || item.carbs || item.Carbs;
      } else if (targetField === 'fiber') {
        return item.fiber || item.Fiber || item.fibre || item.Fibre;
      } else if (targetField === 'sugar') {
        return item.sugar || item.Sugar || item.sugars || item.Sugars;
      } else if (targetField === 'sodium') {
        return item.sodium || item.Sodium || item.salt || item.Salt;
      } else if (targetField === 'ingredients') {
        return item.ingredients || item.Ingredients || item.ingredient_list || item['Ingredient List'];
      } else if (targetField === 'dietary_info') {
        return item.dietary_info || item['Dietary Info'] || item.dietary || item.Dietary;
      } else if (targetField === 'prep_time') {
        return item.prep_time || item['Prep Time'] || item.preparation_time || item['Preparation Time'];
      } else if (targetField === 'cook_time') {
        return item.cook_time || item['Cook Time'] || item.cooking_time || item['Cooking Time'];
      }
      
      return null;
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Review Items</h2>
            <p className="text-gray-600">Select which items to import into your menu.</p>
          </div>
          <div className="flex items-center space-x-4">
            <button onClick={selectAll} className="text-blue-600 hover:text-blue-700 font-medium">
              Select All
            </button>
            <button onClick={selectNone} className="text-gray-600 hover:text-gray-700 font-medium">
              Select None
            </button>
            <span className="text-sm text-gray-500">
              {selectedItems.length} of {analysis.rawData.length} selected
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
          {analysis.rawData.map((item, index) => {
            const name = getFieldValue(item, 'name') || 'Unnamed Item';
            const description = getFieldValue(item, 'description') || 'No description';
            const price = getFieldValue(item, 'price') || '0.00';
            const category = getFieldValue(item, 'category') || 'Uncategorized';
            
            return (
              <div 
                key={index}
                className={`
                  border-2 rounded-lg p-4 cursor-pointer transition-all
                  ${selectedItems.includes(index) 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
                onClick={() => toggleItemSelection(index)}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(index)}
                    onChange={() => toggleItemSelection(index)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <h4 className="font-medium text-gray-900 truncate">
                    {name}
                  </h4>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  {description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-green-600">
                    ${price}
                  </span>
                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                    {category}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-between">
          <button 
            onClick={() => setCurrentStep('analysis')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Back to Analysis
          </button>
          <button 
            onClick={handleImport}
            disabled={selectedItems.length === 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Import {selectedItems.length} Items
          </button>
        </div>
      </div>
    );
  };

  const renderImportingStep = () => (
    <div className="text-center py-12">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Importing Items...</h2>
      <p className="text-gray-600">Please wait while we create your menu items.</p>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg border shadow-sm">
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Bulk Import from Excel/CSV</h1>
              <p className="text-gray-600 mt-1">Import multiple menu items at once with AI assistance</p>
            </div>
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                ← Back
              </button>
            )}
          </div>
        </div>

        <div className="p-6">
          {currentStep === 'upload' && renderUploadStep()}
          {currentStep === 'analysis' && renderAnalysisStep()}
          {currentStep === 'review' && renderReviewStep()}
          {currentStep === 'importing' && renderImportingStep()}
        </div>
      </div>
    </div>
  );
};

export default BulkImportUpload;